// frontend/src/state/analysis/analysisSelectors.js
// Purpose: Provide reusable selector functions for the analysis slice.
// Desired outcome: Allows components to access parts of the Redux state in a memoized way to prevent unnecessary re-renders.

import { createSelector } from '@reduxjs/toolkit';

// Basic state slice selectors
const selectAnalysisState = (state) => state.analysis;

// ✅ Selector for fetching the full calculations state
export const selectCalculations = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations
);

// ✅ Selector for checking if calculations are loading
export const selectCalculationsLoading = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.loading
);

// ✅ Selector for retrieving calculation results
export const selectCalculationResults = createSelector(
  [selectCalculations],
  (calculations) => calculations?.data || { parts: [], summary: {} }
);

// ✅ Selector for retrieving calculation errors
export const selectCalculationsError = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.error
);

// ✅ Selector for calculation history
export const selectCalculationHistory = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.history
);

// ✅ Selector for part matching results
export const selectPartMatchingResults = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.partMatching.results
);

// ✅ Selector for part matching errors
export const selectPartMatchingError = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.partMatching.error
);

// ✅ Computed selector: Checks if calculations are available and not in a loading/error state
export const selectHasValidCalculations = createSelector(
  [selectCalculationResults, selectCalculationsLoading, selectCalculationsError],
  (data, loading, error) => !!data && !loading && !error
);

// ✅ Selector for retrieving calculation readiness check
export const selectCalculationsReadinessCheck = createSelector(
    [selectAnalysisState],
    (analysis) => analysis.calculations.readinessCheck
);

// ✅ Selector for retrieving calculation status
export const selectCalculationsStatus = createSelector(
    [selectAnalysisState],
    (analysis) => analysis.calculations.status
);

// ✅ Selector for mapped columns validation status
export const selectColumnMappingsValidation = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.columnMappings.validationStatus
);

// ✅ Selector for workflow step completion
export const selectWorkflowCompletion = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.workflow.completedSteps
);

// ✅ Selector for workflow validation errors
export const selectWorkflowValidationErrors = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.workflow.validationErrors
);

// ✅ Selectors for scenario_C calculation data
export const selectCalculationsScenarioC = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.scenario_C.data
);

// ✅ Selector for scenario_C loading state
export const selectCalculationsLoadingScenarioC = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.scenario_C.loading
);

// ✅ Selector for scenario_C error state
export const selectCalculationsErrorScenarioC = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.calculations.scenario_C.error
);

// ✅ Computed selector: Checks if scenario_C calculations are available
export const selectHasValidCalculationsScenarioC = createSelector(
  [selectCalculationsScenarioC, selectCalculationsLoadingScenarioC, selectCalculationsErrorScenarioC],
  (data, loading, error) => !!data && !loading && !error
);

// Existing selectors remain unchanged
export const selectStep = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.step
);

export const selectProgress = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.progress
);

export const selectUploadedFiles = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.uploadedFiles
);

export const selectCurrentFile = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.currentFile
);

export const selectFieldMappings = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.fieldMappings
);

export const selectExtractedColumns = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.extractedColumns
);

export const selectColumnMappings = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.columnMappings
);

export const selectPartMatches = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.partMatches
);

export const selectConfidenceThreshold = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.confidenceThreshold
);

export const selectContextualizedParts = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.contextualizedParts
);

export const selectReportData = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.reportData
);

export const selectReports = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.reports
);

// ✅ Selector for report configuration
export const selectReportConfig = createSelector(
  [selectReportData],
  (reportData) => reportData?.reportConfig || {}
);

// ✅ Selector for report generation timestamp
export const selectReportGeneratedAt = createSelector(
  [selectReportData],
  (reportData) => reportData?.generatedAt || null
);

export const selectError = createSelector(
  [selectAnalysisState],
  (analysis) => analysis?.error || null
);

export const selectAvailableCategories = createSelector(
  [selectAnalysisState],
  (analysis) => analysis?.availableCategories || []
);

// OEM parts selectors
export const selectOemParts = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.oemParts
);

export const selectLoadingOemParts = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.loadingOemParts
);

// Loading state selectors
export const selectLoadingCategories = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.loadingCategories
);

export const selectLoadingPartMatches = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.loadingPartMatches
);

// Bootstrap state selectors
export const selectBootstrapInProgress = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.bootstrapInProgress
);

export const selectBootstrapComplete = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.bootstrapComplete
);

export const selectBootstrapResults = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.bootstrapResults
);

export const selectBootstrapError = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.bootstrapError
);

// Computed selector to check if part matching is ready
export const selectIsReadyForPartMatching = createSelector(
  [selectBootstrapComplete, selectAvailableCategories],
  (bootstrapComplete, availableCategories) =>
    bootstrapComplete && availableCategories.length > 0
);

// Module 9: Value Savings

// ✅ Value Savings selectors
export const selectValueSavingsData = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.valueSavings.data
);

export const selectValueSavingsLoading = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.valueSavings.loading
);

export const selectValueSavingsError = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.valueSavings.error
);

export const selectValueSavingsScenarioType = createSelector(
  [selectAnalysisState],
  (analysis) => analysis.valueSavings.scenarioType || 'brownfield'
);

// Modified to handle both nested and root-level assumptions
export const selectValueSavingsAssumptions = createSelector(
  [selectAnalysisState],
  (analysis) => {
    // First try to get the assumptions from the expected nested location
    if (analysis.valueSavings && analysis.valueSavings.assumptions) {
      return analysis.valueSavings.assumptions;
    }
    
    // Fall back to root-level assumptions if they exist
    if (analysis.assumptions) {
      return analysis.assumptions;
    }
    
    // If neither exists, return default assumptions
    return {
      annualHoldingCostPct: 0.20,
      obsolescencePct_current: 0.10,
      obsolescencePct_onDemand: 0.01,
      warehouseSetupCost: 100000,
      annualWarehouseOperationalCost: 50000,
      responsivenessRevenueIncreasePct: 0.02
    };
  }
);

// ✅ Selector for calculation metadata
export const selectCalculationMetadata = createSelector(
  [selectCalculations],
  (calculations) => calculations?.metadata || {}
);